import React from "react";

import useDatasetMappedValue from "hooks/useDatasetMappedValue";
import { DatasetDataInfo } from "models/dataset-models";
import { computeAge, fmtDatetime } from "utils/time-utils";
import { formatDatasetValue } from "utils/value-format-utils";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { I, T } from "services/lang/LanguageService";
import { useDatasetMappedIcon } from "hooks/useDatasetMappedIcon";
import { useDisplayTimezone } from "hooks/useTimezone";
import { DATE_PATTERN_SECONDS } from "utils/constants";
import { Instant } from "@js-joda/core";
import { Divider } from "primereact/divider";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";

type DatasetTooltipProps = {
  data: InstantDoubleValue | null | undefined;
  time: Instant;
  dataset: DatasetDataInfo;
  hide?: boolean;
};

function DatasetTooltipText({ data, time, dataset, hide = false }: DatasetTooltipProps) {
  if (hide) return null;

  const noData = !data;

  return (
    <div style={{ whiteSpace: "pre" }}>
      <ValueDisplay label={I(dataset?.title)} data={data} dataset={dataset} />
      <TimeDisplay label={T("word.measurement-date")} time={time} noData={noData} dataset={dataset} />
    </div>
  );
}

export default DatasetTooltipText;

interface DatasetTooltipTextWithPrevValueProps {
  dataset: DatasetDataInfo;
  hide?: boolean;
  refTime?: Instant;
  refreshRate: number;
}

export const DatasetTooltipTextWithPrevValue = ({
  dataset,
  refTime,
  refreshRate,
  hide = false,
}: DatasetTooltipTextWithPrevValueProps) => {
  const { data } = useGetRecentData([dataset.identifier], {
    refTime: refTime?.toString(),
    count: 2,
    options: {
      refetchInterval: refTime ? 0 : refreshRate * 1000,
    },
  });

  const currData = data?.[0].data[1];
  const prevData = data?.[0].data[0];

  if (hide || !data || !currData || !prevData) return null;

  const noData = !data;

  const currTime = Instant.parse(currData.key);
  const prevTime = Instant.parse(prevData.key);

  return (
    <div style={{ whiteSpace: "pre" }}>
      <b>{I(dataset?.title)}</b>
      <ValueDisplay label={T("word.current-value")} data={currData} dataset={dataset} />
      <TimeDisplay
        label={T("word.current-measurement-date")}
        time={currTime}
        noData={noData}
        dataset={dataset}
      />
      <Divider className="my-1" />
      <ValueDisplay label={T("word.previous-value")} data={prevData} dataset={dataset} />
      <TimeDisplay
        label={T("word.prev-measurement-date")}
        time={prevTime}
        noData={noData}
        dataset={dataset}
        hideAge
      />
    </div>
  );
};

interface ValueDisplayProps {
  label: string;
  data: InstantDoubleValue | null | undefined;
  dataset: DatasetDataInfo;
}

const ValueDisplay = ({ label, data, dataset }: ValueDisplayProps) => {
  return (
    <div>
      <b>{label}: </b>
      {dataset.stringValueMapper ? (
        <MappedValue mapValue={data?.value} mapperId={dataset.stringValueMapper} isTooltip />
      ) : (
        <i>
          {formatDatasetValue(dataset, data)}
          {data?.value && ` ${dataset?.unit}`}
        </i>
      )}
    </div>
  );
};

interface TimeDisplayProps {
  label: string;
  time: Instant;
  noData: boolean;
  dataset?: DatasetDataInfo;
  hideAge?: boolean;
}

const TimeDisplay = ({ label, time, noData, dataset, hideAge }: TimeDisplayProps) => {
  const displayTz = useDisplayTimezone();
  const tagValueAge: React.ReactNode = !hideAge && computeAge(dataset?.loadedTime, time);

  return (
    <div>
      <b>{label}:</b>
      <i>{fmtDatetime(time, displayTz, DATE_PATTERN_SECONDS) || T("word.no-data")}</i>{" "}
      <i>{!noData ? tagValueAge : ""}</i>
    </div>
  );
};

type MappedValueProps = {
  mapValue: number | undefined;
  mapperId: number;
  isTooltip?: boolean;
};

export const MappedValue: React.FC<MappedValueProps> = ({ mapValue, mapperId, isTooltip }) => {
  const { value: initialMappedValue } = useDatasetMappedValue(mapValue, mapperId);
  const mappedValue = typeof initialMappedValue === "string" ? I(initialMappedValue) : initialMappedValue;
  const mappedIcon = useDatasetMappedIcon(mapValue, mapperId);

  if (isTooltip) {
    return (
      <i>{mappedValue ? `${mapValue} - ${mappedValue}` : T("message.no-mapper-value-for", { mapValue })}</i>
    );
  }

  if (mappedIcon) {
    return <i className={mappedIcon as string} />;
  }
  return <i>{mappedValue ?? T("message.no-mapper-value-for", { mapValue })}</i>;
};
